import { ElementSchema, EnumSchema, LVGLSchemaService, PropertySchema } from './LVGLSchemaService';
import { ALL_COLOR_FORMAT_NAMES } from '../../../code_export/generators/lvgl_image/color_format';
import {
    COMPOUND_COORDINATE_NO_CONTENT_TYPE,
    INTEGER_LIKE_TYPES,
    type IntegerLikeType
} from '../../../services/constants';

// TODO: Disallow components from having <param> under <prop>, as it is only for widgets.
// [Functionality Improvement]

// Defining the schema as a record/object structure prevents duplicate keys.
export type LVGLSchema = {
    elements: Record<string, ElementSchema>;
    enums: Record<string, EnumSchema>;
};

const IF_TARGET_PROPERTY: PropertySchema = {
    name: 'if_target',
    type: 'if_target',
    required: false,
    help: 'Targets for tags that can support multiple targets. Defaults to all targets.'
};

const schema: LVGLSchema = {
    elements: {
        /* ROOT ELEMENTS */
        // Component schema
        component: {
            name: 'component',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['consts', 'animations', 'params', 'styles', 'view', 'api', 'previews'],
            order: [['consts', 'fonts', 'images', 'api'], ['styles', 'api'], ['view']]
        },

        // Widget schema
        widget: {
            name: 'widget',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['consts', 'styles', 'params', 'view', 'api', 'previews'],
            order: [['consts', 'fonts', 'images', 'api'], ['styles', 'api'], ['view']]
        },

        // Screen schema
        screen: {
            name: 'screen',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['consts', 'styles', 'animations', 'view'],
            properties: [
                {
                    name: 'name',
                    type: 'string',
                    help: 'Optional screen name. Must be unique across the project.',
                    required: false
                },
                {
                    name: 'permanent',
                    type: 'bool',
                    help: 'If true, screen is created automatically and always exists.\nOtherwise, it is created as needed and deleted when no longer visible.',
                    default: 'false'
                }
            ]
        },

        // Test schema
        test: {
            name: 'test',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['view', 'steps']
        },

        // Project schema
        project: {
            name: 'project',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['folders', 'targets'],
            properties: [
                {
                    name: 'name',
                    type: 'string:project_name',
                    help: 'The name of the project',
                    required: false
                },
                {
                    name: 'lvgl_version',
                    type: 'string:lvgl_version',
                    help: 'LVGL version required by the project',
                    required: true
                },
                {
                    name: 'theme',
                    type: 'enum:theme',
                    help: "The theme applied to the UI. 'default' looks good out of the box; 'simple' applies no styles for easy customization. Defaults to 'default'.",
                    required: false
                },
                {
                    name: 'product_id',
                    type: 'string',
                    help: 'The product_id associated with this project, used for license compatibility checks. Should be set to the product_id of the LVGL license intended to be used with the project.',
                    required: false
                },
                {
                    name: 'git_url',
                    type: 'string',
                    help: 'The git repository URL associated with this project, used for license compatibility checks. Should be set to the git repository URL of the LVGL license intended to be used with the project.',
                    required: false
                }
            ]
        },

        globals: {
            name: 'globals',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [
                'config',
                'consts',
                'api',
                'subjects',
                'styles',
                'images',
                'fonts',
                'const_variants',
                'style_variants'
            ],
            order: [['consts', 'fonts', 'images'], ['styles']]
        },

        translations: {
            name: 'translations',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['translation'],
            properties: [{ name: 'languages', type: 'string[]' }]
        },

        /* CHILD ELEMENTS */

        // Add Styles schema
        styles: {
            name: 'styles',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['style'], // Only allow style tags
            properties: [IF_TARGET_PROPERTY]
        },

        // Add Style schema
        style: {
            name: 'style',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'name', type: 'string', help: 'The name of the style' },
                { name: 'help', type: 'string' }
            ]
        },

        // Add Params and Param schema
        params: {
            name: 'params',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['param']
        },

        // Add Consts schema
        consts: {
            name: 'consts',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['int', 'point', 'color', 'px', 'percentage', 'string', 'opa', 'bool'],
            properties: [IF_TARGET_PROPERTY]
        },

        animations: {
            name: 'animations',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['timeline']
        },

        timeline: {
            name: 'timeline',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['animation', 'include_timeline'],
            properties: [
                {
                    name: 'name',
                    type: 'string'
                },
                {
                    name: 'repeat_delay',
                    type: 'int:non-negative',
                    required: false,
                    help: 'The delay in milliseconds between repeats of the timeline. Only applicable if repeat_count is set.'
                },
                {
                    name: 'repeat_count',
                    type: 'animation:repeat_count',
                    required: false,
                    help: 'The number of times to repeat the timeline. Can be a positive integer or "infinite".'
                }
            ]
        },

        animation: {
            name: 'animation',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                //  TODO: the type should be any lv style property. Is there an enum for that? [Architectural Improvement]
                { name: 'prop', type: 'string' },
                { name: 'target', type: 'lv_obj|self' },
                { name: 'start', type: 'int' },
                { name: 'end', type: 'int' },
                { name: 'duration', type: 'int' },
                { name: 'delay', type: 'int', required: false },
                { name: 'selector', type: 'selector+', required: false },
                { name: 'early_apply', type: 'bool', required: false }
            ]
        },

        include_timeline: {
            name: 'include_timeline',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                {
                    name: 'target',
                    type: 'lv_obj|self'
                },
                {
                    name: 'timeline',
                    type: 'timeline'
                },
                { name: 'delay', type: 'int', required: false }
            ]
        },

        int: {
            name: 'int',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'name', type: 'string' },
                { name: 'value', type: 'int' },
                { name: 'min_value', type: 'int', required: false },
                { name: 'max_value', type: 'int', required: false },
                { name: 'help', type: 'string', required: false }
            ]
        },

        float: {
            name: 'float',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'name', type: 'string' },
                { name: 'value', type: 'float' },
                { name: 'min_value', type: 'float', required: false },
                { name: 'max_value', type: 'float', required: false },
                { name: 'help', type: 'string', required: false }
            ]
        },

        primitive: {
            name: 'primitive',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: []
        },

        color: {
            name: 'color',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'name', type: 'string' },
                { name: 'value', type: 'color' },
                { name: 'help', type: 'string', required: false }
            ]
        },

        px: {
            name: 'px',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'name', type: 'string' },
                { name: 'value', type: 'px' },
                { name: 'help', type: 'string', required: false }
            ]
        },

        point: {
            name: 'point',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'name', type: 'string' },
                { name: 'value', type: 'point' },
                { name: 'help', type: 'string', required: false }
            ]
        },

        percentage: {
            name: 'percentage',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'name', type: 'string' },
                { name: 'value', type: 'percentage' },
                { name: 'help', type: 'string', required: false }
            ]
        },

        string: {
            name: 'string',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'name', type: 'string' },
                { name: 'value', type: 'string' },
                { name: 'help', type: 'string', required: false }
            ]
        },

        pointer: {
            name: 'pointer',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'name', type: 'string' },
                { name: 'value', type: 'image' },
                { name: 'help', type: 'string', required: false }
            ]
        },

        opa: {
            name: 'opa',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'name', type: 'string' },
                { name: 'value', type: 'opa' },
                { name: 'help', type: 'string', required: false }
            ]
        },

        grad: {
            name: 'grad',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'name', type: 'string' },
                { name: 'value', type: 'int' },
                { name: 'help', type: 'string', required: false }
            ]
        },

        bool: {
            name: 'bool',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'name', type: 'string' },
                { name: 'value', type: 'bool' },
                { name: 'help', type: 'string', required: false }
            ]
        },

        previews: {
            name: 'previews',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['preview']
        },

        // Add Preview schema (will be extended with all style_ properties in Schema generation)
        preview: {
            name: 'preview',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['set_subject', 'style'],
            properties: [
                { name: 'name', type: 'string', required: false },
                { name: 'width', type: 'int', required: false },
                { name: 'height', type: 'int', required: false },
                {
                    name: 'zoom',
                    type: 'percentage',
                    help: 'The zoom level of the preview',
                    required: false
                },
                {
                    name: 'language',
                    type: 'string',
                    required: false
                },
                {
                    name: 'styles',
                    type: 'string',
                    required: false
                },
                {
                    name: 'use_target',
                    type: 'if_target:use_target',
                    required: false
                }
            ]
        },

        // Add Set Subject schema
        set_subject: {
            name: 'set_subject',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'name', type: 'subject' },
                { name: 'value', type: 'primitive' }
            ]
        },

        // Add API schema
        api: {
            name: 'api',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['enumdef', 'element', 'prop', 'slot', 'children', 'parts']
        },

        // Documentation-only; ignored by code export.
        parts: {
            name: 'parts',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['part']
        },

        part: {
            name: 'part',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'name', type: 'string' },
                { name: 'help', type: 'string', required: false }
            ]
        },

        children: {
            name: 'children',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [{ name: 'allow', type: 'string' }]
        },

        enumdef: {
            name: 'enumdef',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['enum'],
            properties: [
                { name: 'name', type: 'string' },
                { name: 'help', type: 'string', required: false }
            ]
        },

        element: {
            name: 'element',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['arg', 'prop', 'parts'],
            properties: [
                { name: 'name', type: 'string' },
                { name: 'type', type: 'enum:type', required: false },
                { name: 'help', type: 'string', required: false },
                { name: 'access', type: 'enum:access' }
            ]
        },

        enum: {
            name: 'enum',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'name', type: 'string' },
                {
                    name: 'value',
                    type: 'string',
                    required: false
                },
                { name: 'help', type: 'string', required: false }
            ]
        },

        prop: {
            name: 'prop',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['param'],
            properties: [
                {
                    name: 'name',
                    type: 'string'
                },
                {
                    name: 'type',
                    type: 'enum:type',
                    // TODO: validation for this is a bit complicated 'type' is generally required, unless the prop has a param
                    // [Architectural Improvement]
                    required: false
                },
                {
                    name: 'default',
                    type: 'string',
                    required: false
                },
                {
                    name: 'help',
                    type: 'string',
                    required: false
                }
            ]
        },

        slot: {
            name: 'slot',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                {
                    name: 'name',
                    type: 'string'
                },
                {
                    name: 'help',
                    type: 'string',
                    required: false
                }
            ]
        },

        param: {
            name: 'param',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'name', type: 'string' },
                { name: 'type', type: 'enum:type' },
                { name: 'help', type: 'string', required: false }
            ]
        },

        arg: {
            name: 'arg',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'name', type: 'string' },
                { name: 'type', type: 'enum:type' },
                { name: 'help', type: 'string', required: false },
                {
                    name: 'default',
                    type: 'string',
                    required: false
                }
            ]
        },

        view: {
            name: 'view',
            type: 'lvgl-widget',
            extends: 'lv_obj',
            allowedChildren: 'any',
            properties: [
                {
                    name: 'extends',
                    type: 'string',
                    help: 'The name of the component to extend, the default value is `lv_obj`',
                    default: 'lv_obj'
                },
                IF_TARGET_PROPERTY
            ]
        },

        // Test children

        steps: {
            name: 'steps',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [
                'screenshot_compare',
                'click_at',
                'move_to',
                'press',
                'release',
                'subject_compare',
                'subject_set',
                'wait',
                'freeze',
                'set_language',
                'click_on'
            ]
        },

        screenshot_compare: {
            name: 'screenshot_compare',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [{ name: 'path', type: 'image' }]
        },

        click_at: {
            name: 'click_at',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'x', type: COMPOUND_COORDINATE_NO_CONTENT_TYPE },
                { name: 'y', type: COMPOUND_COORDINATE_NO_CONTENT_TYPE }
            ]
        },

        move_to: {
            name: 'move_to',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'x', type: COMPOUND_COORDINATE_NO_CONTENT_TYPE },
                { name: 'y', type: COMPOUND_COORDINATE_NO_CONTENT_TYPE }
            ]
        },

        press: {
            name: 'press',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: []
        },

        release: {
            name: 'release',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: []
        },

        subject_compare: {
            name: 'subject_compare',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'subject', type: 'subject' },
                { name: 'value', type: 'string' }
            ]
        },

        subject_set: {
            name: 'subject_set',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'subject', type: 'subject' },
                { name: 'value', type: 'string' }
            ]
        },

        wait: {
            name: 'wait',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [{ name: 'ms', type: 'int' }]
        },

        freeze: {
            name: 'freeze',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [{ name: 'ms', type: 'int' }]
        },

        set_language: {
            name: 'set_language',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [{ name: 'name', type: 'string' }]
        },

        click_on: {
            name: 'click_on',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [{ name: 'name', type: 'string' }]
        },

        // Project config

        config: {
            name: 'config',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'name', type: 'string' },
                { name: 'help', type: 'string' }
            ]
        },

        folders: {
            name: 'folders',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['folder']
        },

        folder: {
            name: 'folder',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [{ name: 'path', type: 'string' }]
        },

        targets: {
            name: 'targets',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['target']
        },

        target: {
            name: 'target',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['display', 'memory'],
            properties: [{ name: 'name', type: 'string' }]
        },

        display: {
            name: 'display',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'width', type: 'px' },
                { name: 'height', type: 'px' },
                { name: 'radius', type: 'int', default: '0' },
                {
                    name: 'color_format',
                    type: 'enum:color_format',
                    required: false
                }
            ]
        },

        memory: {
            name: 'memory',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'name', type: 'string' },
                { name: 'size', type: 'string' },
                { name: 'section', type: 'string', required: false },
                { name: 'bandwidth', type: 'string', required: false }
            ]
        },

        fonts: {
            name: 'fonts',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['tiny_ttf', 'bin'],
            properties: [{ name: 'memory', type: 'string', required: false }, IF_TARGET_PROPERTY]
        },

        tiny_ttf: {
            name: 'tiny_ttf',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'name', type: 'string' },
                { name: 'src_path', type: 'string' },
                { name: 'size', type: 'int' },
                {
                    name: 'as_file',
                    type: 'bool',
                    required: false
                }
            ]
        },

        bin: {
            name: 'bin',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'name', type: 'string' },
                { name: 'src_path', type: 'string' },
                { name: 'size', type: 'int' },
                { name: 'as_file', type: 'bool' },
                { name: 'colorinfo', type: 'bool', required: false },
                { name: 'compress', type: 'bool', required: false },
                { name: 'bpp', type: 'int' },
                { name: 'stride', type: 'int', required: false },
                { name: 'align', type: 'int', required: false },
                { name: 'range', type: 'string', required: false },
                { name: 'symbols', type: 'string', required: false },
                { name: 'lv_fallback', type: 'string', required: false }
            ]
        },

        images: {
            name: 'images',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['convert', 'data', 'file'],
            properties: [
                {
                    name: 'sync',
                    type: 'bool',
                    required: false
                },
                {
                    name: 'memory',
                    type: 'string',
                    required: false
                },
                IF_TARGET_PROPERTY
            ]
        },

        data: {
            name: 'data',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'name', type: 'string' },
                { name: 'src_path', type: 'string' },
                {
                    name: 'file_format',
                    type: 'string',
                    required: false
                },
                {
                    name: 'color_format',
                    type: 'enum:color_format',
                    required: true
                }
            ]
        },

        file: {
            name: 'file',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'name', type: 'string' },
                { name: 'src_path', type: 'string' },
                { name: 'file_format', type: 'string', required: false }
            ]
        },

        convert: {
            name: 'convert',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'src', type: 'string' },
                { name: 'dest', type: 'string' },
                { name: 'width', type: 'string', required: false },
                { name: 'height', type: 'string', required: false },
                { name: 'scale', type: 'string', required: false },
                { name: 'color_format', type: 'enum:image_convert_color_format', required: false }
            ]
        },

        subjects: {
            name: 'subjects',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: ['int', 'string', 'float', 'pointer']
        },

        // Translation schema
        translation: {
            name: 'translation',
            type: 'lvgl-element',
            extends: '',
            allowedChildren: [],
            properties: [
                { name: 'tag', type: 'string' },
                { name: 'char_count', type: 'int', required: false },
                { name: 'comment', type: 'string', required: false }
            ]
        }

        // Special / meta types
        // TODO: there are a variety of special types that should be listed here for visibility,
        // even though they are only actually used in validation and autocomplete
    },

    enums: {
        /* LVGL ENUMS */
        access: {
            values: [{ name: 'add' }, { name: 'get' }, { name: 'set' }, { name: 'custom' }]
        },

        bool: {
            values: [{ name: 'true' }, { name: 'false' }]
        },

        theme: {
            values: [{ name: 'default' }, { name: 'simple' }]
        },

        type: {
            values: [
                { name: 'string' },
                { name: 'string[]' },
                { name: 'bool' },
                { name: 'color' },
                { name: 'color[]' },
                ...INTEGER_LIKE_TYPES.map((name: IntegerLikeType): { name: IntegerLikeType } => ({ name })),
                { name: 'int[]' },
                { name: 'opa' },
                { name: 'grad' },
                { name: 'obj' },
                { name: 'point' },
                { name: 'arglist' },
                { name: 'subject' },
                { name: 'subject[]' },
                { name: 'font' },
                { name: 'font[]' },
                { name: 'image' },
                { name: 'image[]' },
                { name: 'lv_obj' },
                { name: 'style' },
                { name: 'style_prop' },
                { name: 'event_cb' },
                { name: 'lv_event' },
                { name: 'screen' }
                // Anything starting with 'enum:' is a reference to an enum definition,
                // and also allowed as a type. That's coded directly in the XML validator.
            ]
        },

        color_format: {
            values: ALL_COLOR_FORMAT_NAMES.map((name) => ({ name }))
        },

        image_convert_color_format: {
            values: [
                { name: 'rgb565' },
                { name: 'rgb565a8' },
                { name: 'rgb888' },
                { name: 'xrgb8888' },
                { name: 'argb8888' },
                { name: 'a8' }
            ]
        }
    }
};

// The default interface should be a function which takes
// an LVGLSchemaService and adds all the element and enum
// definitions to it.
export default (schemaService: LVGLSchemaService) => {
    for (const [key, element] of Object.entries(schema.elements)) {
        schemaService.elements.set(key, element);
    }

    for (const [key, enumDef] of Object.entries(schema.enums)) {
        schemaService.enums.set(key, enumDef);
    }
};

// Will check all parts of the built-in schema (as opposed to that loaded from LVGL) for required attributes.
export const SCHEMA_ELEMENTS_TO_CHECK_FOR_REQUIRED_ATTRIBUTES: string[] = Object.keys(schema.elements);
